import { compose } from 'redux';
import { connect } from 'react-redux';
import TRCQuestionsView from './TRCQuestionsView';
import AvatarView from '../../../common/avatar/avatarView';
import withAvatar from '../../../common/avatar/withAvatar';
import {
    cleanUpBook,
    goToNextPage,
    narrateScreen,
    onSelectAnswer,
    fetchTRCContent,
    onQuestionChange,
    completeTRCBookResult,
    saveComprehensionResponse,
    // saveTROComprehensionAndReadingTimeDuration,
    updateSelectedComprehensionResponses,
    setComprehensionComplete,
    resetPagesViewed,
    setComprehensionAttempt,
    increaseAttemptCount,
} from '../../trcActions';
import {
    trackStartTroQuestions,
    trackAnsweredTroQuestion,
    trackTroComprehensionBackButton,
    trackTroVrPopup,
} from '../../trcReporting';
import { getQuestionIndex } from '../../../common/utils/urlParsers';
import selectors from '../../../rootSelectors';
import {
    unmountScreen,
    activateNextButton,
    deactivateNextButton,
    unblockScreen,
    blockScreen,
    setConfirmPopupVisibility,
    hideHelpPopup,
} from '../../../common/screen/screenActions';
import { stopAudio, narrateReset } from '../../../common/audio/audioActions';
import {
    resetQuestionsAndTimeTrackingState,
    setIsQuestionCompleted,
} from '../../../common/questions/questionsActions';
import { goToQuestion } from '../../../common/navigation/navigationActions';
import { TRC, TRO } from '../../../constants/measure';
import onNextButtonClick from './TRCQuestionsActions';
import { getTrcInstruction, getTrcCaption } from '../../troConstants';
import { cancelBackgroundRetry } from '../../../common/api/apiCallActions';
import { logUserOut } from '../../../common/user/userActions';
import {
    stopTimeTracking,
    startTimeTracking,
    stopQuestionTimeTracking,
    startQuestionTimeTracking,
} from '../../../common/timeTracking/timeTrackingActions';
import { TRO_COMPREHENSION_SECTION } from '../../../common/timeTracking/timeTrackingKeys';
import {
    executeApiCallFailedEffect,
    executeApiCallSucceededEffect,
} from '../../../common/api/trcoActions';

const NAVIGATION_STEP = 1;
const instructionsKey = 'comprehensionReplay';
const comprehensionReadyInsctruction = 'comprehensionReady';

const mapStateToProps = (state, ownProps) => {
    const questionIndex = getQuestionIndex(ownProps.match.params) - 1;
    const comprehensionItem = selectors.getTRCComprehensionItem(state, questionIndex);
    return {
        questionIndex,
        comprehensionItem,
        isScreenBlocked: selectors.getIsScreenBlocked(state),
        isGreyedOut: selectors.getIsScreenGreyedOut(state),
        isDataFetched: selectors.getIsTRCDataFetched(state),
        isFirstTRCPage: selectors.isFirstTRCPage(state),
        isLastTRCPage: selectors.isLastTRCPage(state),
        pageNum: selectors.getActiveTRCPage(state),
        pages: selectors.getTRCPages(state),
        isNextButtonActive: selectors.getIsNextButtonActive(state),
        isBackButtonActive: selectors.getIsBackButtonActive(state),
        TRCSelectedResponse: selectors.getTRCSelectedResponse(state, questionIndex),
        selectedIndex: selectors.getSelectedIndex(state),
        totalQuestions: selectors.getNumTRCComprehensionItems(state),
        isConfirmPopupActive: selectors.isConfirmPopupActive(state),
        bookLevel: selectors.getTRCReadingLevel(state),
        indented: selectors.getIsBookIndented(state),
        isFirstBook: selectors.getIsFirstTRCBook(state),
        isHelpPopupVisible: selectors.getIsHelpPopupVisible(state),
        resultsVRStatuses: selectors.getVRResultsStatus(state),
        isComprehensionComplete: selectors.getIsComprehensionComplete(state),
        currentResult: selectors.findResultFor(state, { measure: TRO.MEASURE, isComplete: false }),
        isRetrying: selectors.getIsBackgroundRetryInProgress(state),
        isOfflineAudioSyncInProgress: selectors.getIsOfflineAudioSyncInProgress(state),
        isVrResultProcessing: selectors.getIsVrResultProcessing(state),
        isVrResultProcessingTimeout: selectors.getIsVrResultProcessingTimeout(state),
        resultVRErrorReasons: selectors.getVRErrorReasons(state),
        bookId: selectors.getTRCBookId(state),
        bookTitle: selectors.getTRCBookTitle(state),
        didApiCallSucceed: selectors.getDidApiCallSucceed(state),
        didApiCallFail: selectors.getDidApiCallFail(state),
        prevApiRequest: selectors.getPrevApiRequest(state),
        isExternalURLSourcePresent: selectors.getExternalURLSourceStatus(state),
        arLanguage: selectors.getArLanguage(state),
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    const questionIndex = getQuestionIndex(ownProps.match.params) - 1;
    return {
        logUserOut,
        fetchContent: () =>
            dispatch(fetchTRCContent()).then(() => dispatch(startQuestionTimeTracking())),
        selectAnswer: (responseIndex, questionType, maxPossibleResponses) => {
            dispatch(
                onSelectAnswer(responseIndex, questionIndex, questionType, maxPossibleResponses),
            );
        },
        onClassificationChange: (classifiedResponseIdsMatrix, isComplete) => {
            dispatch(
                updateSelectedComprehensionResponses(questionIndex, classifiedResponseIdsMatrix),
            );
            return isComplete ? dispatch(activateNextButton()) : dispatch(deactivateNextButton());
        },
        setIsQuestionCompleted: isComplete => {
            dispatch(setIsQuestionCompleted(questionIndex, isComplete));
        },
        onNext: () => {
            dispatch(blockScreen());
            dispatch(stopQuestionTimeTracking());
            dispatch(setComprehensionAttempt(questionIndex));

            return dispatch(onNextButtonClick(questionIndex)).then(() => {
                dispatch(unblockScreen());
            });
        },
        onBack: (isExternalURLSourcePresent) => {
            const prevQuestionIndex = questionIndex - 1;
            const prevQuestion = prevQuestionIndex + 1;
            if (!isExternalURLSourcePresent) dispatch(trackTroComprehensionBackButton(questionIndex));
            dispatch(increaseAttemptCount(prevQuestionIndex));
            dispatch(onQuestionChange(prevQuestionIndex));
            dispatch(goToQuestion(TRC, null, prevQuestion));
        },
        goNextBookPage: () => {
            dispatch(goToNextPage());
        },
        goPrevBookPage: () => {
            dispatch(goToNextPage(-NAVIGATION_STEP));
        },
        confirmBookComplete: async (isComprehensionComplete, bookId) => {
            const isLast = true;
            dispatch(setConfirmPopupVisibility(false));
            dispatch(blockScreen());

            if (isComprehensionComplete) {
                // TODO: add this back once "add_book_stats" op is migrated to the TRO lambda API
                // await dispatch(saveTROComprehensionAndReadingTimeDuration());
                return dispatch(completeTRCBookResult());
            }

            return dispatch(saveComprehensionResponse(questionIndex, isLast)).then(async () => {
                dispatch(setComprehensionComplete(bookId));
                dispatch(cancelBackgroundRetry());
                // await dispatch(saveTROComprehensionAndReadingTimeDuration());
                return dispatch(completeTRCBookResult());
            });
        },
        hideHelpPopup: () => {
            dispatch(hideHelpPopup());
        },
        narrateScreen: (isExternalURLSourcePresent, arLanguage) => {
            const instruction = getTrcInstruction(isExternalURLSourcePresent, comprehensionReadyInsctruction, arLanguage);
            const caption = getTrcCaption(isExternalURLSourcePresent, comprehensionReadyInsctruction, arLanguage);
            return dispatch(narrateScreen(instruction, caption));
        },
        returnToBook: () => {
            dispatch(setConfirmPopupVisibility(false));
        },
        cleanup: () => {
            dispatch(cleanUpBook());
            dispatch(resetQuestionsAndTimeTrackingState());
            dispatch(stopAudio());
            dispatch(narrateReset());
            dispatch(unmountScreen());
        },
        startSingleQuestionAndComprehensionTimers: () => {
            dispatch(startQuestionTimeTracking());
            return dispatch(startTimeTracking(TRO_COMPREHENSION_SECTION));
        },
        stopComprehensionTimeTracking: () => dispatch(stopTimeTracking(TRO_COMPREHENSION_SECTION)),
        executeApiCallSucceededEffect: (prevApiRequest, resultId, bookId) =>
            dispatch(
                executeApiCallSucceededEffect({
                    prevApiRequest,
                    resultId,
                    bookId,
                }),
            ),
        executeApiCallFailedEffect: (prevApiRequest, resultId, bookId) =>
            dispatch(
                executeApiCallFailedEffect({
                    prevApiRequest,
                    resultId,
                    bookId,
                }),
            ),
        trackStartTroQuestions: () => dispatch(trackStartTroQuestions()),
        trackAnsweredTroQuestion: () => dispatch(trackAnsweredTroQuestion(questionIndex)),
        resetPagesViewed: () => dispatch(resetPagesViewed()),
        trackTroVrPopup: (problemText, bookId, bookTitle) => dispatch(trackTroVrPopup(problemText, bookId, bookTitle)),
    };
};

const avatarMapStateToProps = state => ({
    position: AvatarView.Position.LOWER_LEFT,
    instructions: getTrcInstruction(selectors.getExternalURLSourceStatus(state), instructionsKey, selectors.getArLanguage(state)),
});

const TRCQuestionsContainer = compose(
    connect(mapStateToProps, mapDispatchToProps),
    withAvatar(avatarMapStateToProps),
)(TRCQuestionsView);

export default TRCQuestionsContainer;
